import { apiClient } from './api';

export interface AiCalorieEstimate {
  foodDescription: string;
  response: string;
}

export interface AiChatResponse {
  message: string;
  response: string;
}

export interface AiModel {
  name: string;
  size: number | null;
  modifiedAt: string | null;
}

export interface AiStatus {
  status: 'available' | 'unavailable';
  models: AiModel[];
  error?: string;
}

export interface AiPullModelResponse {
  status: string;
  model: string;
}

export interface AiSuggestExercisesInput {
  programId: number;
  dayNumber?: number;
  goal?: string;
  muscleGroups?: string[];
  message?: string;
}

export interface AiSuggestExercisesResponse {
  response: string;
  programId: number;
  dayNumber: number | null;
  exerciseCount: number;
}

export interface AiBuildProgramInput {
  programId: number;
  daysCount?: number;
  splitType?: string;
  goal?: string;
  difficulty?: string;
  message?: string;
}

export interface AiBuildProgramResponse {
  response: string;
  programId: number;
  daysCount: number;
  exerciseCount: number;
}

export interface AiSuggestedExercise {
  exerciseId: number;
  exerciseName: string;
  reason: string;
  sets: number;
  reps: number;
  dayNumber: number;
}

export interface AiSuggestionsResult {
  suggestions: AiSuggestedExercise[];
  explanation: string;
}

export interface AiProgramDaySuggestion {
  dayNumber: number;
  dayName: string;
  exercises: {
    exerciseId: number;
    exerciseName: string;
    sets: number;
    reps: number;
    order: number;
  }[];
}

export interface AiBuildProgramResult {
  days: AiProgramDaySuggestion[];
  splitDescription: string;
}

export class AiService {
  private static readonly ENDPOINT = '/api/ai';

  static async estimateCalories(foodDescription: string): Promise<AiCalorieEstimate> {
    const response = await apiClient.post<AiCalorieEstimate>(
      `${this.ENDPOINT}/estimate-calories`,
      { foodDescription }
    );
    return response.data;
  }

  static async chat(message: string, context: 'fitness' | 'nutrition' = 'fitness'): Promise<AiChatResponse> {
    const response = await apiClient.post<AiChatResponse>(
      `${this.ENDPOINT}/chat`,
      { message, context }
    );
    return response.data;
  }

  static async getStatus(): Promise<AiStatus> {
    const response = await apiClient.get<AiStatus>(`${this.ENDPOINT}/status`);
    return response.data;
  }

  static async pullModel(model: string = 'tinyllama'): Promise<AiPullModelResponse> {
    const response = await apiClient.post<AiPullModelResponse>(
      `${this.ENDPOINT}/pull-model`,
      { model }
    );
    return response.data;
  }

  static async suggestExercises(input: AiSuggestExercisesInput): Promise<AiSuggestExercisesResponse> {
    const response = await apiClient.post<AiSuggestExercisesResponse>(
      `${this.ENDPOINT}/suggest-exercises`,
      input
    );
    return response.data;
  }

  static async buildProgram(input: AiBuildProgramInput): Promise<AiBuildProgramResponse> {
    const response = await apiClient.post<AiBuildProgramResponse>(
      `${this.ENDPOINT}/build-program`,
      input
    );
    return response.data;
  }

  static parseExerciseSuggestions(rawResponse: string): AiSuggestionsResult | null {
    try {
      const jsonMatch = rawResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.suggestions && Array.isArray(parsed.suggestions)) {
          return parsed as AiSuggestionsResult;
        }
      }
    } catch {
      // Not valid JSON
    }
    return null;
  }

  static parseBuildProgramResult(rawResponse: string): AiBuildProgramResult | null {
    try {
      const jsonMatch = rawResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.days && Array.isArray(parsed.days)) {
          parsed.days = parsed.days.map((day: any) => ({
            ...day,
            exercises: Array.isArray(day.exercises) ? day.exercises : [],
          }));
          return parsed as AiBuildProgramResult;
        }
      }
    } catch {
      // Not valid JSON
    }
    return null;
  }
}

export default AiService;
